import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import type {
  FinishWorkoutOptions,
  WorkoutExercise,
  WorkoutSet,
} from "../../../shared/src";
import {
  ApiError,
  deleteWorkout,
  finishWorkout,
  getProgram,
  getTrainingDay,
  getWorkout,
  listDayExercises,
  listExercises,
  listWorkoutExercises,
  listWorkoutSets,
  removeExerciseFromWorkout,
  reorderAdhocExercises,
} from "../api-client";
import { AdHocExerciseCard } from "../components/AdHocExerciseCard";
import { AddExercisePicker } from "../components/AddExercisePicker";
import { ExerciseCard } from "../components/ExerciseCard";
import { OfflineIndicator } from "../components/OfflineIndicator";
import { RestTimerBanner } from "../components/RestTimerBanner";
import { SortableAdHocItem } from "../components/SortableAdHocItem";
import { WorkoutCompareBand } from "../components/WorkoutCompareBand";
import { useRestTimer } from "../hooks/useRestTimer";
import {
  effectiveRestSeconds,
  loadDefaultSeconds,
  saveDefaultSeconds,
} from "../storage/restTimerPrefs";

function toDatetimeLocal(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function WorkoutDetail() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const validId = Number.isInteger(id) && id > 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const workoutQuery = useQuery({
    queryKey: ["workout", id] as const,
    queryFn: () => getWorkout(id),
    enabled: validId,
  });

  const trainingDayId = workoutQuery.data?.training_day_id ?? null;

  const dayQuery = useQuery({
    queryKey: ["trainingDay", trainingDayId] as const,
    queryFn: () => getTrainingDay(trainingDayId as number),
    enabled: trainingDayId !== null,
  });

  const programId = dayQuery.data?.program_id ?? null;

  const programQuery = useQuery({
    queryKey: ["program", programId] as const,
    queryFn: () => getProgram(programId as number),
    enabled: programId !== null,
  });

  const dayExercisesQuery = useQuery({
    queryKey: ["dayExercises", trainingDayId] as const,
    queryFn: () => listDayExercises(trainingDayId as number),
    enabled: trainingDayId !== null,
  });

  const setsQuery = useQuery({
    queryKey: ["workoutSets", id] as const,
    queryFn: () => listWorkoutSets(id),
    enabled: validId,
  });

  const workoutExercisesQuery = useQuery({
    queryKey: ["workoutExercises", id] as const,
    queryFn: () => listWorkoutExercises(id),
    enabled: validId,
  });

  const catalogQuery = useQuery({
    queryKey: ["exercises", { includeArchived: false }] as const,
    queryFn: () => listExercises(),
    enabled: validId,
  });

  const removeExerciseMutation = useMutation({
    mutationFn: (exerciseId: number) =>
      removeExerciseFromWorkout(id, exerciseId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workoutSets", id] });
      await queryClient.invalidateQueries({
        queryKey: ["workoutExercises", id],
      });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: number[]) => reorderAdhocExercises(id, ids),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["workoutExercises", id],
      });
    },
  });

  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
  );

  function onAdhocDragEnd(event: DragEndEvent, currentIds: number[]) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = currentIds.indexOf(Number(active.id));
    const toIndex = currentIds.indexOf(Number(over.id));
    if (fromIndex < 0 || toIndex < 0) return;
    const next = arrayMove(currentIds, fromIndex, toIndex);
    queryClient.setQueryData<WorkoutExercise[]>(
      ["workoutExercises", id],
      (prev) => {
        if (!prev) return prev;
        const plannedIds = new Set(
          (dayExercisesQuery.data ?? []).map((dx) => dx.exercise_id),
        );
        const planned = prev.filter((e) => plannedIds.has(e.exercise_id));
        const adhocByid = new Map(
          prev
            .filter((e) => !plannedIds.has(e.exercise_id))
            .map((e) => [e.exercise_id, e]),
        );
        const reorderedAdhoc = next
          .map((eid) => adhocByid.get(eid))
          .filter((e): e is WorkoutExercise => e !== undefined);
        return [...planned, ...reorderedAdhoc];
      },
    );
    reorderMutation.mutate(next);
  }

  const finishMutation = useMutation({
    mutationFn: (opts: FinishWorkoutOptions | undefined) =>
      opts === undefined ? finishWorkout(id) : finishWorkout(id, opts),
    onSuccess: async ({ workout, bumps }) => {
      queryClient.setQueryData(["workout", id], workout);
      await queryClient.invalidateQueries({ queryKey: ["activeWorkout"] });
      if (bumps.length > 0) {
        await queryClient.invalidateQueries({ queryKey: ["dayExercises"] });
        await queryClient.invalidateQueries({ queryKey: ["trainingDay"] });
      }
      navigate("/dashboard", bumps.length > 0 ? { state: { bumps } } : undefined);
    },
  });

  const [finishInputs, setFinishInputs] = useState<{
    started_at: string;
    ended_at: string;
    originalStartedAt: string;
    originalEndedAt: string;
  } | null>(null);

  useEffect(() => {
    if (finishInputs !== null) return;
    const workout = workoutQuery.data;
    if (!workout || workout.ended_at !== null) return;
    const startInit = toDatetimeLocal(workout.started_at);
    const endInit = "";
    setFinishInputs({
      started_at: startInit,
      ended_at: endInit,
      originalStartedAt: startInit,
      originalEndedAt: endInit,
    });
  }, [workoutQuery.data, finishInputs]);

  const startMs = finishInputs?.started_at
    ? Date.parse(finishInputs.started_at)
    : NaN;
  const endMs = finishInputs?.ended_at
    ? Date.parse(finishInputs.ended_at)
    : NaN;
  const invalidFinishTimes =
    Number.isFinite(startMs) && Number.isFinite(endMs) && endMs < startMs;

  function onFinishClicked(): void {
    if (!finishInputs) {
      finishMutation.mutate(undefined);
      return;
    }
    const opts: FinishWorkoutOptions = {};
    if (
      finishInputs.started_at !== "" &&
      finishInputs.started_at !== finishInputs.originalStartedAt
    ) {
      opts.started_at = new Date(finishInputs.started_at).toISOString();
    }
    if (
      finishInputs.ended_at !== "" &&
      finishInputs.ended_at !== finishInputs.originalEndedAt
    ) {
      opts.ended_at = new Date(finishInputs.ended_at).toISOString();
    }
    finishMutation.mutate(Object.keys(opts).length > 0 ? opts : undefined);
  }

  const cancelMutation = useMutation({
    mutationFn: () => deleteWorkout(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["activeWorkout"] });
      queryClient.removeQueries({ queryKey: ["workout", id] });
      queryClient.removeQueries({ queryKey: ["workoutSets", id] });
      queryClient.removeQueries({ queryKey: ["workoutExercises", id] });
      navigate("/dashboard");
    },
  });

  function onCancelClicked(): void {
    const setCount = setsQuery.data?.length ?? 0;
    const message =
      setCount === 0
        ? "Cancel this workout? It will be deleted."
        : `Cancel this workout? ${setCount} logged ${
            setCount === 1 ? "set" : "sets"
          } will be lost.`;
    if (window.confirm(message)) {
      cancelMutation.mutate();
    }
  }

  const [defaultRest, setDefaultRest] = useState<number>(() =>
    loadDefaultSeconds(),
  );
  const timer = useRestTimer(playRestEndCue);
  const handleSetAdded = useCallback(
    (exerciseId: number) => {
      timer.start(effectiveRestSeconds(exerciseId, defaultRest));
    },
    [timer, defaultRest],
  );
  function updateDefaultRest(seconds: number) {
    const safe = Math.max(0, Math.floor(seconds));
    setDefaultRest(safe);
    saveDefaultSeconds(safe);
  }

  if (!validId) {
    return (
      <main className="min-h-dvh p-6">
        <p className="text-sm text-red-400" role="alert">
          Invalid workout id.
        </p>
      </main>
    );
  }

  return (
    <main className="min-h-dvh p-6 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Workout</h1>
        <div className="flex items-center gap-2">
          <RestDefaultControl
            seconds={defaultRest}
            onChange={updateDefaultRest}
          />
          <Link
            to="/dashboard"
            className="text-sm text-slate-400 hover:text-slate-200"
          >
            ← Dashboard
          </Link>
        </div>
      </header>

      {workoutQuery.isLoading && (
        <p className="text-sm text-slate-400">Loading…</p>
      )}
      {workoutQuery.isError && (
        <p className="text-sm text-red-400" role="alert">
          Failed to load this workout.
        </p>
      )}

      {workoutQuery.data && (
        <>
          <OfflineIndicator workoutId={id} />

          <p className="text-sm text-slate-300" data-testid="workout-header">
            {trainingDayId === null
              ? "Ad-hoc workout"
              : programQuery.data && dayQuery.data
                ? `${programQuery.data.name} — ${dayQuery.data.name}`
                : "Loading workout details…"}
          </p>

          <WorkoutCompareBand workoutId={id} />

          <section aria-label="workout exercises" className="space-y-3">
            {trainingDayId !== null &&
              dayExercisesQuery.data &&
              dayExercisesQuery.data.length === 0 && (
                <p className="text-sm text-slate-400">
                  No exercises planned for this day yet.
                </p>
              )}
            {dayExercisesQuery.data?.map((dx) => (
              <ExerciseCard
                key={`planned-${dx.id}`}
                workoutId={id}
                exerciseId={dx.exercise_id}
                exerciseName={dx.exercise_name}
                sets={setsForExercise(setsQuery.data ?? [], dx.exercise_id)}
                targets={{
                  sets: dx.target_sets,
                  reps: dx.target_reps,
                  weight_kg: dx.target_weight_kg,
                }}
                disabled={workoutQuery.data.ended_at !== null}
                onSetAdded={handleSetAdded}
              />
            ))}
            {(() => {
              const adhocItems = adHocFor(
                workoutExercisesQuery.data ?? [],
                dayExercisesQuery.data ?? [],
              );
              const adhocIds = adhocItems.map((a) => a.exercise_id);
              const finished = workoutQuery.data.ended_at !== null;
              if (adhocItems.length === 0) return null;
              return (
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => onAdhocDragEnd(e, adhocIds)}
                >
                  <SortableContext
                    items={adhocIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {adhocItems.map((adhoc) => (
                      <SortableAdHocItem
                        key={`adhoc-${adhoc.exercise_id}`}
                        id={adhoc.exercise_id}
                        exerciseName={adhoc.exercise_name}
                        disabled={finished || adhocItems.length < 2}
                      >
                        <AdHocExerciseCard
                          workoutId={id}
                          exerciseId={adhoc.exercise_id}
                          exerciseName={adhoc.exercise_name}
                          sets={setsForExercise(
                            setsQuery.data ?? [],
                            adhoc.exercise_id,
                          )}
                          onSetAdded={handleSetAdded}
                          onRemove={() =>
                            confirmRemoveAdHoc(
                              adhoc.exercise_name,
                              () =>
                                removeExerciseMutation.mutate(
                                  adhoc.exercise_id,
                                ),
                            )
                          }
                          disabled={finished}
                        />
                      </SortableAdHocItem>
                    ))}
                  </SortableContext>
                </DndContext>
              );
            })()}
            {workoutQuery.data.ended_at === null && (
              <AddExercisePicker
                workoutId={id}
                catalog={catalogQuery.data ?? []}
                excluded={new Set(
                  [
                    ...(dayExercisesQuery.data ?? []).map((dx) => dx.exercise_id),
                    ...(workoutExercisesQuery.data ?? []).map(
                      (we) => we.exercise_id,
                    ),
                  ],
                )}
                onAdded={handleSetAdded}
              />
            )}
          </section>

          <RestTimerBanner timer={timer} />

          <section className="space-y-2 pb-24">
            {workoutQuery.data.ended_at === null && finishInputs && (
              <div className="space-y-2 rounded-md border border-slate-800 bg-slate-900 p-3">
                <label className="block text-xs text-slate-400">
                  Started at
                  <input
                    type="datetime-local"
                    value={finishInputs.started_at}
                    onChange={(e) =>
                      setFinishInputs((prev) =>
                        prev
                          ? { ...prev, started_at: e.target.value }
                          : prev,
                      )
                    }
                    className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100"
                  />
                </label>
                <label className="block text-xs text-slate-400">
                  Ending at
                  <input
                    type="datetime-local"
                    value={finishInputs.ended_at}
                    onChange={(e) =>
                      setFinishInputs((prev) =>
                        prev
                          ? { ...prev, ended_at: e.target.value }
                          : prev,
                      )
                    }
                    className="mt-1 block w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100"
                  />
                </label>
                {invalidFinishTimes && (
                  <p role="alert" className="text-sm text-red-400">
                    End time must be after start time
                  </p>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={onFinishClicked}
              disabled={
                finishMutation.isPending ||
                cancelMutation.isPending ||
                workoutQuery.data.ended_at !== null ||
                invalidFinishTimes
              }
              className="w-full rounded-md bg-orange-600 px-4 py-3 text-base font-medium text-white hover:bg-orange-500 disabled:opacity-50"
            >
              {workoutQuery.data.ended_at !== null
                ? "Workout finished"
                : finishMutation.isPending
                  ? "Finishing…"
                  : "Finish workout"}
            </button>
            {finishMutation.isError && (
              <p className="text-sm text-red-400" role="alert">
                {messageFor(finishMutation.error)}
              </p>
            )}
            {workoutQuery.data.ended_at === null && (
              <button
                type="button"
                onClick={onCancelClicked}
                disabled={
                  cancelMutation.isPending || finishMutation.isPending
                }
                className="w-full rounded-md border border-red-900 px-4 py-2 text-sm text-red-300 hover:bg-red-950 disabled:opacity-50"
              >
                {cancelMutation.isPending ? "Cancelling…" : "Cancel workout"}
              </button>
            )}
            {cancelMutation.isError && (
              <p className="text-sm text-red-400" role="alert">
                {messageFor(cancelMutation.error)}
              </p>
            )}
          </section>
        </>
      )}
    </main>
  );
}

function RestDefaultControl({
  seconds,
  onChange,
}: {
  seconds: number;
  onChange: (s: number) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="rest timer settings"
        className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
      >
        Rest: {seconds}s
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-56 rounded-md border border-slate-700 bg-slate-900 p-3 shadow-lg">
          <p className="text-xs text-slate-400 mb-2">Default rest length</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onChange(Math.max(0, seconds - 15))}
              className="rounded-md border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800"
            >
              -15
            </button>
            <span className="flex-1 text-center text-base tabular-nums">
              {seconds}s
            </span>
            <button
              type="button"
              onClick={() => onChange(seconds + 15)}
              className="rounded-md border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800"
            >
              +15
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function setsForExercise(
  all: WorkoutSet[],
  exerciseId: number,
): WorkoutSet[] {
  return all
    .filter((s) => s.exercise_id === exerciseId)
    .sort((a, b) => a.set_index - b.set_index);
}

function adHocFor(
  workoutExercises: WorkoutExercise[],
  planned: { exercise_id: number }[],
): WorkoutExercise[] {
  const plannedIds = new Set(planned.map((p) => p.exercise_id));
  return workoutExercises.filter(
    (we) => we.source === "ad-hoc" && !plannedIds.has(we.exercise_id),
  );
}

function confirmRemoveAdHoc(name: string, onConfirm: () => void): void {
  if (
    window.confirm(
      `Remove "${name}" from this workout? All logged sets for it will be deleted.`,
    )
  ) {
    onConfirm();
  }
}

function playRestEndCue(): void {
  try {
    const AudioCtx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 880;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        ctx.currentTime + 0.3,
      );
      osc.start();
      osc.stop(ctx.currentTime + 0.35);
    }
  } catch {
    // ignore — audio is best-effort
  }
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      navigator.vibrate(200);
    }
  } catch {
    // ignore
  }
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Try again.";
}
